import { motion } from 'framer-motion';
import CircularProgress, { circularProgressClasses } from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import useScreenType from '../../hooks/utility/useScreenType';
import Theme from '../Theme';
import useStorefront from '../../modules/storefront/hooks/useStorefront';
import { useMemo } from 'react';

interface Props {
  color: string;
  logo?: string | null;
}

const Spinner = ({ size, spinColor }: { size: number; spinColor: string }) => (
  <Box sx={{ position: 'relative', display: 'inline-flex' }} key="spinner">
    <CircularProgress
      size={size}
      thickness={2}
      style={{
        color: spinColor,
      }}
      sx={{
        opacity: 0.2,
      }}
      value={100}
      variant="determinate"
      key="determinate"
    />

    <CircularProgress
      variant="indeterminate"
      sx={{
        animationDuration: '550ms',
        position: 'absolute',
        left: 0,
        [`& .${circularProgressClasses.circle}`]: {
          strokeLinecap: 'round',
        },
      }}
      size={size}
      thickness={2}
      style={{
        color: spinColor,
      }}
    />
  </Box>
);

const LoadingApp = (props: Props): JSX.Element | null => {
  const { color } = props;

  const { isDesktop, isTablet } = useScreenType();
  const { loadingConfig } = useStorefront();
  const spinColor = loadingConfig?.primary_color || color || Theme.color.gray['500'];
  const logo = loadingConfig?.logo || props?.logo || '';

  const size = useMemo(() => {
    if (isDesktop) {
      return 180;
    }

    if (isTablet) {
      return 160;
    }

    return 140;
  }, [isDesktop, isTablet]);

  return (
    <motion.div
      className="w-screen h-screen flex justify-center items-center p-md fixed inset-0 bg-brand-background-primary"
      style={{ zIndex: 10000, backgroundColor: loadingConfig?.background_color }}
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.7 }}
    >
      <div className="relative flex items-center justify-center">
        {logo && (
          <motion.div
            className="absolute"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div className="lg:h-[60px] lg:w-[60px] h-[50px] w-[50px] relative flex items-center justify-center">
              <img
                src={logo}
                alt={'restaurant_logo'}
                className="object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          </motion.div>
        )}
        <div className="absolute" key={Theme.color.gray['500']}>
          <Spinner size={size} spinColor={spinColor} />
        </div>
      </div>
    </motion.div>
  );
};

export default LoadingApp;
